import React, { createContext, Component } from 'react';
import PropTypes from 'prop-types';
import ErrorTracker from 'error_tracker';
import ApiRequest from 'core/api/request';
import { parseError } from 'core/api/utils';
import { promiser, CustomerStatus } from 'core/utils';

// Max number of carts per mp_order
// TODO: carts?.slice(0, MAX_CARTS) when fetch pre-order
const MAX_CARTS = 10;

const mpOrderContextDefaultProps = {
	cartIds: [], // cart(s) user is checking out with
	mpOrderId: null, // populated once order is placed/confirmed
	carts: [], // Array of `Cart` Objects
	orders: [], // Array of `Order` Objects
	guest: false, // Bool -- is guest checkout. Assume false until the user selects guest checkout
	customer: null, // Object { id, email, name, firstName, ...customer }
	customerPay: null, // Object { lastFour, cardType, expMonth, expYear, ...customerPay }
	shipAddress: null, // Object { city, state, street, zipCode, ...address }
	shipAddressId: null, // `CustomerAddress.id` posted to orders api -- should only get populated by front-end
	walletEntryId: null, // posted to orders api -- should only get populated by front-end
	guestEmail: "", // posted when creating guest customer
	// ui state
	completed: false, // is order completed? aka do we have `mpOrderId`?
	calculated: false, // Set automatically by `setShipAddress`. If ship addresses hasn't changed since last ship calc
	loading: true, // True, when fetching order details (for confirmation)
	processing: false, // True, when placing the order (from "Place Order" pressed, until order `completed`)
	error: null, // Object { status, message }
	samePaymentAddress: true, // Is billing address (name, zip, country) same as shipping?
};

const MpOrderContext = createContext({ ...mpOrderContextDefaultProps });
MpOrderContext.displayName = 'MpOrderContext';

export class MpOrderProvider extends Component {
	state = {
		...mpOrderContextDefaultProps,
		...this.props.value,
	}

	componentDidMount() {
		this.fetchMpOrder();
	}

	/**
	 * Fetches info for completed order with cart data (to populate the checkout page)
	 * Sets order details in state
	 *
	 * @return {Promise}
	 */
	fetchMpOrder = async () => {
		const { mpOrderId } = this.state;
		if (!mpOrderId) {
			// No orderId for Context to fetch.
			// Skip fetch, and build draft order to submit
			console.debug('No order to fetch for MpOrderContext');
			this.setState({ loading: false });
			return;
		}
		const [err, response] = await promiser(
			ApiRequest.get(`/mp_orders/${mpOrderId}/`, {
				with_carts: "t",
			})
		);

		if (err) {
			ErrorTracker.captureException(err);
			const { fetchResponse, ...e } = err;
			const { status } = fetchResponse;
			let error = {
				status,
				message: parseError(e),
			};

			if (status === 404) {
				error.message = "Order not found";
			}

			this.setState({ error, loading: false });
			return;
		}

		const mpOrder = response || {};
		const customer = (mpOrder?.orders?.length > 0 && mpOrder.orders[0].customer?.id)
			? { ...mpOrder.orders[0].customer }
			: null;

		ErrorTracker.addBreadcrumb({
			category: 'mp_orders',
			message: 'MpOrderContext: fetchMpOrder results',
			data: {
				mp_order_id: mpOrder?.id,
				cart_ids: mpOrder?.carts?.map(c => c.id),
				order_ids: mpOrder?.orders?.map(o => o.id),
			},
		});

		this.setState({
			mpOrderId: mpOrder?.id,
			carts: mpOrder?.carts || [],
			orders: mpOrder?.orders || [],
			guest: customer?.status === CustomerStatus.guest || customer?.status === CustomerStatus.claimed,
			customer: customer,
			customerPay: mpOrder?.transaction?.id ? {
				...mpOrder?.transaction?.customerPay
			} : null,
			shipAddress: mpOrder?.shipAddress?.id ? {
				...mpOrder.shipAddress
			} : null,
			loading: false,
			completed: !!mpOrder?.id && mpOrder?.orders?.length > 0,
		});
	}

	/**
	 * Call this to save/post orders
	 * Sets new order details in state
	 *
	 * @param {Object} giftInfo {giftMessage, giftRecipientName, giftRecipientEmail, giftMessageSendOption, deliveryMethod, occasion, relationship, giftMessageSendAt}
	 * @param {Number} serviceFee calculated for all carts
	 * @param {Bool} isGift
	 * @return {Promise} Resolves with Array[error, mpOrder]
	 */
	postMpOrder = async ({
		giftInfo = {},
		serviceFee = 0,
	}) => {
		const { shipAddressId, walletEntryId, cartIds, guest } = this.state;

		// Very basic validation here, rest should be handled outside of this call
		if (!shipAddressId || !walletEntryId) {
			const errMsg = `Missing required ${!shipAddressId ? 'shipping address' : 'payment method'}`;
			return [errMsg];
		}
		if (!(cartIds?.length > 0)) {
			return ["Missing required cart"];
		}

		const [err, order] = await promiser(
			ApiRequest.post(`/orders/`, {
				shipAddressId: shipAddressId,
				walletEntryId: walletEntryId,
				cartIds: cartIds,
				serviceFee: serviceFee,
				serviceFeeRate: window?.cj?.service_fee_rate,
				giftInfo,
				guest,
				// TODO: Remove when feature is fully out:
				allowMulticart: true,
			})
		);

		if (err) {
			return [parseError(err)];
		}

		// TODO: This should setState after backend returns MpOrder
		return [null, order];
	}

	/**
	 * Call to explicitly save address
	 * Will avoid saving if invalid
	 * BUT Does NOT handle validation
	 */
	saveAddress = async () => {
		const { shipAddressId, shipAddress, guest } = this.state;
		const { country, state, zipCode, street } = shipAddress;

		let valid = false;
		if (country === "US") {
			// required zip and state for US addresses
			if (state && zipCode) valid = true;
		} else {
			// save non-US addresses no matter what, maybe optimize
			// probably at least have a street field
			if (street) valid = true;
		}

		if (!valid) {
			// TODO: We should validate before we call this
			return [`Address missing required fields`];
		}

		const method = shipAddressId ? "put" : "post";
		const url = shipAddressId ? `/addresses/${shipAddressId}/` : `/addresses/`;

		const payload = { guest, ...shipAddress };
		const [err, address] = await promiser(ApiRequest[method](url, payload));

		if (err) {
			return [err];
		}

		// This could trigger `useAddressModelSave`
		this.setShipAddress(address);

		return [null, address];
	}

	saveWallet = async ({ storeId = null, testMode = false }) => {
		const { walletEntryId, customerPay, guest, cartIds } = this.state;
		const { name, number, expMonth, expYear, cvc, zipCode, country, paymentMethodId } = customerPay;

		if (walletEntryId) {
			// using a saved wallet, not adding a new one
			return [null, customerPay];
		}

		const [err, wallet] = await promiser(
			ApiRequest.post(`/wallet/`, {
				name,
				number,
				expMonth,
				expYear,
				cvc,
				zipCode,
				country,
				pmId: paymentMethodId, // when using google/apple pay
				// meta:
				guest,
				storeId, // will only be populated for single-store checkout
				testMode,
				cjpMulticart: cartIds?.length > 1,
			})
		);

		if (err) {
			return [err];
		}

		this.setCustomerPay(wallet);

		return [null, wallet];
	}

	setCartIds = (cartIds) => {
		this.setState({
			cartIds: [ ...cartIds ],
		});
	}

	setGuestBool = (setBool) => {
		this.setState({ guest: setBool });
	}

	setCustomer = (customer) => {
		// TODO: Perhaps we should have a `UserContext`
		this.setState({
			customer: { ...customer },
			guest: customer?.status === CustomerStatus.guest,  // Enum 1 = guest, 2 = previously guest but changed pass (claimed), 3 = registered (normal signup/ acct creation)
		});
	}

	setGuestEmail = (guestEmail) => {
		this.setState({ guestEmail });
	}

	setProcessing = (processing=true) => {
		this.setState({ processing });
	}

	setSamePaymentAddress = (samePaymentAddress) => {
		this.setState({ samePaymentAddress });
	}

	/**
	 * Registers a guest under the email given for billing. To be used in checkout before an order is made.
	 * @returns {Array} [error, customer]
	 */
	registerGuest = async () => {
		const { guestEmail, customerPay,  shipAddress } = this.state;

		if (!guestEmail) {
			return [null, null];
		}

		const [err, customer] = await promiser(
			ApiRequest.post(`/customer/register_guest`, {
				email: guestEmail,
				name: customerPay.name || shipAddress.to,  // Use customerPay.name for when billing name != shipname, otherwise shipAddress.to
			})
		);

		if (err) {
			return [err, customer];
		}

		this.setCustomer(customer);

		return [null, customer];
	}

	/**
	 * Registers given array of cartIds to the current customer from set cookie.
	 * Will error if no customer is set.
	 * @param {Array} [[errors], [carts]]
	 */
	registerCarts = async (cartIds=[]) => {
		const errors = [];
		const carts = [];

		for (const cartId of cartIds) {
			const [cartRegisterErr, cartRegistered] = await promiser(ApiRequest.put(`/carts/${cartId}/claim`));
			errors.push(cartRegisterErr);
			carts.push(cartRegistered);
		}

		return [errors, carts];
	}

	setShipAddress = (customerAddress) => {
		this.setState((prevState) => {
			// Assume we calculated shipping already if we have the same relevant parts
			// Calc done via `useAutoCalculateShipping` along with `useAddressModelSave` triggering the update
			//
			// Example Scenario:
			// 1. `setShipAddress` call made
			// 1. `useAutoCalculateShipping` may call `calculateTaxShip`
			// 1. `useAddressModelSave` calls `saveAddress`
			// 1. `saveAddress` response is set via `setShipAddress`
			// *** THIS *** 1. `calculated` may be set
			// 1. `useAutoCalculateShipping` may call `calculateTaxShip` (if not `calculated`)
			const { shipAddress } = prevState;
			const { country, state, zipCode, street } = (customerAddress?.address || {});
			// Assume its calculated when the relevants fields are unchanged
			const calculated = (
				shipAddress?.country === country && shipAddress?.state === state && shipAddress?.zipCode === zipCode && shipAddress?.street === street
			);
			return {
				shipAddressId: customerAddress?.id || null,
				shipAddress: customerAddress?.address || null,
				calculated: calculated,
			};
		});
	}

	setCustomerPay = (customerPay) => {
		this.setState({
			walletEntryId: customerPay?.id,
			customerPay: { ...customerPay },
		});
	}

	render() {
		const {
			state,
			setCartIds,
			setGuestBool,
			setGuestEmail,
			setShipAddress,
			setCustomerPay,
			setCustomer,
			setProcessing,
			setSamePaymentAddress,
			postMpOrder,
			saveAddress,
			saveWallet,
			registerGuest,
			registerCarts,
		} = this;
		const { children } = this.props;
		const value = {
			...state,
			setCartIds,
			setGuestBool,
			setGuestEmail,
			setShipAddress,
			setCustomerPay,
			setCustomer,
			setProcessing,
			setSamePaymentAddress,
			postMpOrder,
			saveAddress,
			saveWallet,
			registerGuest,
			registerCarts
		};

		return (
			<MpOrderContext.Provider value={value}>
				{children}
			</MpOrderContext.Provider>
		);
	}
}

MpOrderProvider.sharedPropTypes = {
	cartIds: PropTypes.array,
	mpOrderId: PropTypes.number,
	carts: PropTypes.arrayOf(PropTypes.object),
	orders: PropTypes.arrayOf(PropTypes.object),
	guest: PropTypes.bool,
	customer: PropTypes.object,
	customerPay: PropTypes.object,
	shipAddress: PropTypes.object,
	shipAddressId: PropTypes.number,
	walletEntryId: PropTypes.number,
	guestEmail: PropTypes.string,
	loading: PropTypes.bool,
};

MpOrderProvider.propTypes = {
	children: PropTypes.node.isRequired,
	value: PropTypes.shape({
		...MpOrderProvider.sharedPropTypes,
	}),
};

MpOrderProvider.defaultProps = {
	value: {
		...mpOrderContextDefaultProps,
	},
};

/*
* Provide children down the tree with access to this data
*/
export const MpOrderConsumer = MpOrderContext.Consumer;

export default MpOrderContext;
